// src/admin/screens/loans/tabs/PaymentsTab.tsx
// Payment history for this loan. Schema alignment: our DB uses direction
// 'disbursement' | 'repayment' (not inbound/outbound), completed_at instead of
// received_at, and provider/provider_ref instead of method/reference.
import DataTable, { type Column } from '@admin/components/DataTable';
import StatusChip from '@admin/components/StatusChip';
import Money from '@admin/components/Money';
import Chip from '@admin/components/Chip';
import SectionHead from '@admin/components/SectionHead';
import { formatCatDateTime } from '@admin/utils/dates';
import type { Ngwee } from '@/lib/types';
import type { Database } from '@/lib/database.types';
import type { LoanDetail } from '@admin/hooks/useLoan';

type Row = LoanDetail['payments'][number];
type PaymentStatus = Database['public']['Enums']['payment_status'];

const STATUS_TONE: Record<PaymentStatus, 'info' | 'positive' | 'warn' | 'danger' | 'neutral'> = {
  initiated: 'info',
  pending: 'info',
  completed: 'positive',
  failed: 'danger',
  timeout: 'warn',
  reconciled: 'positive',
};

function whenOf(r: Row): string {
  return formatCatDateTime(r.completed_at ?? r.initiated_at);
}

export default function PaymentsTab({ loan }: { loan: LoanDetail }) {
  const rows = [...loan.payments].sort((a, b) => {
    const ta = (a.completed_at ?? a.initiated_at) || '';
    const tb = (b.completed_at ?? b.initiated_at) || '';
    return tb.localeCompare(ta);
  });

  const completed = rows.filter((p) => p.status === 'completed' || p.status === 'reconciled').length;
  const description = rows.length === 0
    ? 'No payments recorded yet.'
    : `${rows.length} payment${rows.length === 1 ? '' : 's'} · ${completed} completed`;

  const cols: Column<Row>[] = [
    { key: 'at', header: 'When', render: whenOf, width: '180px' },
    {
      key: 'dir',
      header: 'Direction',
      render: (r) => (
        <Chip tone={r.direction === 'repayment' ? 'success' : 'brand'} size="sm">
          {r.direction === 'repayment' ? 'Repayment' : 'Disbursement'}
        </Chip>
      ),
    },
    {
      key: 'amt',
      header: 'Amount',
      render: (r) => <Money amount={r.amount_ngwee as Ngwee} />,
      align: 'right',
    },
    {
      key: 'st',
      header: 'Status',
      render: (r) => <StatusChip label={r.status} tone={STATUS_TONE[r.status]} />,
    },
    {
      key: 'ref',
      header: 'Reference',
      render: (r) => (
        <span className="mono" style={{ fontSize: 12.5, color: 'var(--c-text-muted)' }}>
          {r.provider_ref ?? '—'}
        </span>
      ),
    },
    { key: 'provider', header: 'Provider', render: (r) => r.provider },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SectionHead
        eyebrow="Money flow"
        title="Payments"
        description={description}
      />
      <DataTable
        rows={rows}
        columns={cols}
        rowKey={(r) => r.id}
        emptyLabel="No payments yet."
      />
    </div>
  );
}
